import React from 'react';
import { Route, Routes, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization } from '../../../context/Localization/localization';
import { Heading } from '../../../controls/Heading/Heading';
import { reverseEnum } from '../../../helpers/enum';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { SearchForm } from './SearchForm/SearchForm';
import { PathName } from '../../../helpers/Routes';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { SearchFormProps } from './SearchForm/SearchForm.types';

export enum AppointmentLookupMethod {
    Unknown = 'unknown',
    Name = 'name',
    PhoneNumber = 'phonenumber',
    Business = 'business',
    PhoneOrEmail = 'phoneoremail'
}

const reverseAppointmentLookup = reverseEnum(AppointmentLookupMethod);

export interface IProps extends SearchFormProps { }

export const Lookup: React.FC<IProps> = React.memo((props) => {
    const Localization = useLocalization();
    const { pathname } = useResolvedPath('.');
    const navigate = useNavigate();

    const onLookupMethodSelected = (lookup = AppointmentLookupMethod.Unknown) => {
        if (lookup !== AppointmentLookupMethod.Unknown) {
            navigate(PathName(pathname, lookup));
        }
    };

    const selection = () => (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={Localization.Excellent}
                    secondaryHeading={Localization.PleaseSelectAppointmentLookup}
                    roundedIcon={
                        <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                            <GMSizedIcon
                                iconType={'Calendar'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.CalendarIcon}
                            />
                        </GMRoundedIcon>
                    }
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                onClick={() => onLookupMethodSelected(AppointmentLookupMethod.Name)}
                                styleType={'primary'}
                                label={Localization.Name}
                                title={Localization.ClickToSearchByLastName}
                                kioskOptions={{
                                    square: true,
                                }}
                                id={Localization.ClickToSearchByLastName}
                                ariaLabel={Localization.ClickToSearchByLastName}
                            />
                        ),
                        (
                            <CTAButton
                                onClick={() => onLookupMethodSelected(AppointmentLookupMethod.PhoneNumber)}
                                styleType={'primary'}
                                label={Localization.PhoneNumber}
                                title={Localization.ClickToSearchByPhoneNumber}
                                kioskOptions={{
                                    square: true,
                                }}
                                id={Localization.ClickToSearchByPhoneNumber}
                                ariaLabel={Localization.ClickToSearchByPhoneNumber}
                            />
                        ),
                        (
                            <CTAButton
                                onClick={() => onLookupMethodSelected(AppointmentLookupMethod.Business)}
                                styleType={'primary'}
                                label={Localization.Business}
                                title={Localization.ClickToSearchByBusinessName}
                                kioskOptions={{
                                    square: true,
                                }}
                                id={Localization.ClickToSearchByBusinessName}
                                ariaLabel={Localization.ClickToSearchByBusinessName}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
        />
    );

    return (
        <Routes>
            <Route path={''} element={selection()} />
            <Route path={':lookup/*'} element={<LookupRoute onAppointmentSelected={props.onAppointmentSelected} onNotListed={props.onNotListed} />} />
        </Routes>
    );
});

const LookupRoute: React.FC<Omit<IProps, 'lookupMethod' | 'initialEmail'>> = (props) => {
    const params = useParams<{ lookup: AppointmentLookupMethod; }>();
    const lookupMethod = AppointmentLookupMethod[reverseAppointmentLookup[params.lookup as keyof typeof reverseAppointmentLookup]];

    if (lookupMethod !== AppointmentLookupMethod.Unknown) {
        return (
            <SearchForm
                lookupMethod={lookupMethod}
                onAppointmentSelected={props.onAppointmentSelected}
                onNotListed={props.onNotListed}
            />
        );
    }
    else {
        return null;
    }
};
